import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CrewMerchantApi } from 'corso-types';

import api from '~/api';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { isErrorMessage } from '~/utils/errorResponse';

const queryKeyPrefix = 'discountDefinition';

export function useDiscountDefinitions() {
  const storeId = useStoreId();
  return useQuery({
    queryKey: [queryKeyPrefix, storeId],
    initialData: [],
    queryFn: () => api.store(storeId).discountDefinitions.list(),
  });
}

export function useDiscountDefinitionCreate() {
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (
      body: CrewMerchantApi.RequestBody<
        '/:storeId/discount-definition',
        'post'
      >,
    ) => api.store(storeId).discountDefinitions.create(body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeyPrefix, storeId] }),
  });
}

export function useDiscountDefinitionDelete() {
  const storeId = useStoreId();
  const queryClient = useQueryClient();

  const toast = useToast();

  return useMutation({
    mutationFn: (id: number) =>
      api.store(storeId).discountDefinitions.delete(id),
    onSuccess: (response) => {
      // The delete endpoint returns HTTP 200 with `{ success, clientMessage }`
      // even when the delete was refused (e.g. the definition is still
      // referenced by an automation rule). Surface the message and skip
      // invalidation in that case.
      if (!response.success) {
        toast.show(response.clientMessage);
        return undefined;
      }
      return queryClient.invalidateQueries({
        queryKey: [queryKeyPrefix, storeId],
      });
    },
    onError: (error) => {
      const message = isErrorMessage(error)
        ? error.message
        : 'Something went wrong. Please try again.';

      toast.show(message);

      return error;
    },
  });
}
